import React from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {generatePath} from 'react-router-dom';

import {ReactComponent as SettingsIcon} from 'assets/svg/options.svg';
import {Page} from 'components/common/Page';
import {PermissionAccess} from 'components/common/PermissionAccess';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {CheckboxArray} from 'components/ui-kit/CheckboxArray';
import {Icon} from 'components/ui-kit/Icon';
import {Input} from 'components/ui-kit/Input';
import {Link} from 'components/ui-kit/Link';
import {Modal} from 'components/ui-kit/Modal';
import {ContentContainer} from 'components/ui-kit/PageComponents';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {onSetEditUser} from 'models/customs/users/table';
import {
    customsList$,
    customsUserIdForRemoveConfirmation$,
    onChangeCustomsUserIdForRemoveConfirmation,
    removeCustomsUserFx,
} from 'models/profile/customsUsers/table';
import {UserPermission} from 'types';
import {CustomsRow, CustomsUserRow, StaffAccessors as Accessors} from 'types/profile';
import {handleNil} from 'utils/handleNil';

import {ProfileNavigationTabs} from '../NavigationTabs';
import {Wrapper} from '../styled';

import {
    Action,
    ActionsContainer,
    CancelConfirmButton,
    ConfirmRemoveFooter,
    ControlsWrapper,
    CustomsCellWrapper,
    HeaderControls,
    SubmitConfirmButton,
} from './styled';
import {CustomsUsersGridTable} from './Table';
import {TableSearch} from './Table/SearchFilter';

export function convertData(data: CustomsUserRow[] | undefined) {
    if (!data) return [];

    return data.map((elem) => ({
        fullName: handleNil(
            `${elem.firstName ?? ''} ${elem.middleName ?? ''} ${elem.lastName ?? ''}`.trim(),
        ),
        email: handleNil(elem.email),
        phone: handleNil(elem.phone),
        customs: handleNil(elem.customs, (value: Required<CustomsRow>[]) => (
            <CustomsCellWrapper>
                {value.map((c) => (
                    <li>
                        <Link to={generatePath(ROUTE.CUSTOMS_USERS, {id: c.id})}>{c.name}</Link>
                    </li>
                ))}
            </CustomsCellWrapper>
        )),
        actions: handleNil(elem, (value: CustomsUserRow) => (
            <ActionsContainer>
                {/*<Action*/}
                {/*    onClick={() =>*/}
                {/*        value.userId && onChangeCustomsUserIdForRemoveConfirmation(value.userId)*/}
                {/*    }*/}
                {/*>*/}
                {/*    <Icon source={TrashIcon} fillColor="#F54848" width={18} height={18} />*/}
                {/*</Action>*/}
                <Action
                    onClick={() => {
                        onSetEditUser(value);
                        history.push(ROUTE.CUSTOMS_USER_UPDATE);
                    }}
                >
                    <Icon source={SettingsIcon} fillColor="#000" width={18} height={18} />
                </Action>
            </ActionsContainer>
        )),
    }));
}

const getConfig = (t: TFunction, permissions?: UserPermission[]) => [
    {
        id: 'fullName',
        header: t('pages.customsUsers.fields.fullName'),
    },
    {
        id: 'email',
        header: t('pages.customsUsers.fields.email'),
        filter: <Input placeholder={t('Enter value')} />,
        isSortable: true,
    },
    {
        id: 'phone',
        header: t('pages.customsUsers.fields.phoneNumber'),
        maxWidth: 150,
        filter: <Input placeholder={t('Enter value')} />,
        isSortable: true,
    },
    {
        id: 'customs',
        header: t('pages.customsUsers.fields.customs'),
        filter: (
            <CheckboxArray
                searchable={true}
                placeholder="Enter the name or address"
                name="customs-filter"
                options={() => customsList$.getState() ?? []}
            />
        ),
    },
    ...(permissions?.includes(UserPermission.CUSTOMS_EMPLOYEE_EDIT)
        ? [
              {
                  id: Accessors.actions,
                  header: t('pages.customsUsers.fields.actions'),
                  width: 60,
              },
          ]
        : []),
];

export const CustomsUsersPage = () => {
    const {t} = useTranslation();
    const userId = useStore(customsUserIdForRemoveConfirmation$);

    const headerControls = (
        <HeaderControls>
            <ControlsWrapper>
                <TableSearch />
                <PermissionAccess permissions={[UserPermission.CUSTOMS_EMPLOYEE_CREATE]}>
                    <Button onClick={() => history.push(ROUTE.CUSTOMS_USER_CREATE)}>
                        {t('pages.customsUsers.buttons.create')}
                    </Button>
                </PermissionAccess>
            </ControlsWrapper>
        </HeaderControls>
    );

    const ConfirmRemoveUser = () => {
        const onRemoveUser = async () => {
            if (!userId) return;
            await removeCustomsUserFx(userId);
            onChangeCustomsUserIdForRemoveConfirmation(null);
        };
        const translate = (key: string) => t('pages.customsUsers.confirm.' + key);
        return (
            <Modal modalWidth={0} show={!!userId} hideCloseButton onClose={() => {}}>
                <p style={{textAlign: 'center'}}>{translate('message')}</p>
                <ConfirmRemoveFooter>
                    <CancelConfirmButton
                        variant={ButtonVariant.Outline}
                        onClick={() => onChangeCustomsUserIdForRemoveConfirmation(null)}
                    >
                        {translate('cancel')}
                    </CancelConfirmButton>
                    <SubmitConfirmButton onClick={onRemoveUser}>
                        {translate('ok')}
                    </SubmitConfirmButton>
                </ConfirmRemoveFooter>
            </Modal>
        );
    };

    return (
        <Page title={t('pages.profile.title')}>
            <Wrapper>
                <ProfileNavigationTabs />
                <ContentContainer noPaddings>
                    <CustomsUsersGridTable
                        configFunc={getConfig}
                        dataMapper={convertData}
                        headerControls={headerControls}
                    />
                </ContentContainer>
            </Wrapper>
            {userId && <ConfirmRemoveUser />}
        </Page>
    );
};
